import { NavLink, useLocation, useNavigationType } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, House, NotebookPen, Plus, Settings, TrendingUp } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useEffect, useLayoutEffect, useState } from "react";
import type { ReactNode } from "react";
import { useConnection } from "../hooks/useConnection.ts";
import * as api from "../lib/api.ts";
import { ROLE_LABEL, displayName } from "../lib/profile.ts";
import { pendingLabel } from "../lib/pending.ts";
import { resumePath } from "../lib/resume.ts";
import { screenOf } from "../lib/screen.ts";
import { Avatar } from "./Avatar.tsx";
import { LiveBar } from "./LiveBar.tsx";
import { StartSheet } from "./StartSheet.tsx";

/**
 * Navigation sits at the bottom on the phone — inside thumb reach, clear of
 * the home indicator — and on the left on a desktop. A tablet or a narrow
 * window gets a rail of icons; only a wide screen has room for the rail to say
 * where the block stands instead of being four links and empty space.
 *
 * Both disappear on the exercise screen: mid-set, nothing competes with the
 * weight and the validate button.
 *
 * The navigation stays white: the session's colour belongs to the session
 * — its title, its validate button — and put on every icon and tab it no
 * longer marked anything.
 *
 * Three places and an action. Starting a session is not a place: the middle
 * button opens the choice over the current screen, or goes back to the
 * session under way.
 */

const LINKS: { to: string; label: string; icon: LucideIcon; also?: string }[] = [
  { to: "/", label: "Aujourd'hui", icon: House },
  // A notebook, not a clock: the journal is what was written down.
  { to: "/historique", label: "Journal", icon: NotebookPen },
  { to: "/progression", label: "Progrès", icon: TrendingUp },
];

// Réglages sits behind the avatar: whose app this is, and where it is set up.
const ACCOUNT = { to: "/reglages/compte", also: "/reglages", label: "Compte" };

export function Chrome({ children }: { children: ReactNode }) {
  const location = useLocation();
  const focused = location.pathname.includes("/exercice/");
  // Here and not only in the navigation: a movement's screen shows none, and
  // sets queued there waited for the way back to the session to be sent.
  const connection = useConnection();
  useScreenStart(location.pathname);
  useViewportRepair();
  const running = useRunningSession();

  return (
    <div
      className={
        focused
          ? "min-h-dvh text-ink"
          : "min-h-dvh text-ink md:grid md:grid-cols-[4.5rem_1fr] lg:grid-cols-[13rem_1fr] xl:grid-cols-[15rem_1fr]"
      }
    >
      {/* Behind everything, in the colours of the session on screen. */}
      <div className="aurora" aria-hidden>
        <i />
      </div>
      {!focused && <SideNav />}
      <main className={focused ? "" : running ? "min-w-0 pb-20 md:pb-0" : "min-w-0 pb-20 md:pb-0"}>{children}</main>
      {!focused && running && (
        <LiveBar
          session={running}
          onSessionScreen={location.pathname === `/seance/${running.id}`}
          home={location.pathname === "/"}
          pending={pendingLabel(connection.pending, connection.syncing)}
        />
      )}
      {!focused && !running && <PendingPill label={pendingLabel(connection.pending, connection.syncing)} />}
      {!focused && <BottomNav />}
    </div>
  );
}

/** The session under way, if any: what the middle button and the live bar are about. */
function useRunningSession() {
  const current = useQuery({ queryKey: ["session", "current"], queryFn: api.getCurrentSession });
  return current.data?.session?.status === "in_progress" ? current.data.session : null;
}

/**
 * A new screen opens at its top. The router keeps the page where it was, so
 * the next movement opened scrolled down to where the last one's validate
 * button was. Going back is left alone: the browser puts it back itself.
 */
function useScreenStart(pathname: string) {
  const navigation = useNavigationType();
  const screen = screenOf(pathname);
  useLayoutEffect(() => {
    if (navigation === "POP") return;
    window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen]);
}

/**
 * iOS, as a home-screen app, does not always give the page its height back
 * when the keyboard closes: the bottom bar is left floating mid-screen over
 * a gap. Scrolling to where the page already is makes it lay the fixed bars
 * out again. Measured from the visual viewport: it is the one that shrinks
 * for the keyboard.
 */
function useViewportRepair() {
  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;
    let keyboard = false;
    const onResize = () => {
      const open = window.innerHeight - viewport.height > 120;
      if (keyboard && !open) window.scrollTo(window.scrollX, window.scrollY);
      keyboard = open;
    };
    viewport.addEventListener("resize", onResize);
    return () => viewport.removeEventListener("resize", onResize);
  }, []);
}

/**
 * The middle of the navigation: back to the session under way, or the sheet
 * to start one. On the rail it is a square; wide, it says which.
 */
function StartAction({ variant }: { variant: "tab" | "rail" }) {
  const running = useRunningSession();
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  if (variant === "tab") {
    const lit = pathname.startsWith("/seance/");
    const disc = (
      <span className="flex h-11 w-11 items-center justify-center rounded-full bg-ink text-[var(--color-base)] shadow-md">
        {running ? (
          <ArrowRight size={20} strokeWidth={2.5} aria-hidden />
        ) : (
          <Plus size={22} strokeWidth={2.5} aria-hidden />
        )}
      </span>
    );
    const label = (
      <span className={["text-[0.7rem] font-medium", lit ? "text-ink" : "text-ink-mute"].join(" ")}>
        {running ? "Reprendre" : "Démarrer"}
      </span>
    );
    return running ? (
      <NavLink to={resumePath(running.id)} className="flex h-14 flex-col items-center justify-center gap-0.5">
        {disc}
        {label}
      </NavLink>
    ) : (
      <>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex h-14 w-full flex-col items-center justify-center gap-0.5"
        >
          {disc}
          {label}
        </button>
        {open && <StartSheet onClose={() => setOpen(false)} />}
      </>
    );
  }

  const className =
    "group mt-6 flex h-11 w-11 items-center justify-center rounded-card bg-ink text-[var(--color-base)] lg:flex lg:h-auto lg:w-full lg:items-start lg:justify-start lg:gap-0 lg:px-4 lg:py-3.5 lg:text-left";
  const inner = (lead: string, name: string, icon: ReactNode) => (
    <>
      <span className="lg:hidden">{icon}</span>
      <span className="hidden min-w-0 lg:block">
        <span className="block text-[0.66rem] font-bold tracking-[0.12em] uppercase opacity-65">{lead}</span>
        <span className="wide mt-0.5 block truncate text-[1rem] font-bold">{name}</span>
      </span>
    </>
  );
  return running ? (
    <NavLink to={resumePath(running.id)} title={running.name} className={className}>
      {inner("En cours", running.name, <ArrowRight size={18} aria-hidden />)}
    </NavLink>
  ) : (
    <>
      <button type="button" onClick={() => setOpen(true)} title="Démarrer une séance" className={className}>
        {inner("Nouvelle séance", "Démarrer", <Plus size={18} aria-hidden />)}
      </button>
      {open && <StartSheet onClose={() => setOpen(false)} />}
    </>
  );
}

function SideNav() {
  const { pathname } = useLocation();
  const { online, pending, syncing } = useConnection();
  const block = useQuery({ queryKey: ["block"], queryFn: api.getCurrentBlock });

  const current = block.data?.block;

  return (
    <nav className="sticky top-0 hidden h-dvh border-r border-line/70 bg-surface/60 md:flex md:flex-col md:items-center md:px-3 md:py-7 lg:items-stretch lg:px-5">
      <div className="display text-[1.35rem] font-bold">
        <span className="lg:hidden">T</span>
        <span className="hidden lg:inline">Training</span>
      </div>

      {/* The block, up top, because it is the frame everything else sits in. */}
      {current && (
        <div className="mt-7 hidden lg:block">
          <p className="label truncate">{current.name}</p>
          <p className="mt-1.5 flex items-baseline gap-1.5">
            <span className="display text-[2.2rem] font-bold text-ink">{current.weekIndex}</span>
            <span className="num text-[0.9rem] text-ink-mute">/ {current.weeks} semaines</span>
          </p>
          <div className="mt-3 flex gap-0.5">
            {current.attendance.map((week, index) => (
              <span
                key={week.week}
                className={[
                  "h-1.5 flex-1 rounded-[1px]",
                  week.done >= week.planned
                    ? "bg-ink"
                    : week.done > 0
                      ? "bg-ink/40"
                      : index + 1 <= current.weekIndex
                        ? "bg-iron/50"
                        : "bg-surface-high",
                ].join(" ")}
              />
            ))}
          </div>
        </div>
      )}

      <ul className="mt-8 space-y-1 lg:mt-7 lg:space-y-0.5">
        {LINKS.map((link) => {
          const lit = link.also !== undefined && pathname.startsWith(link.also);
          return (
            <li key={link.to}>
              <NavLink
                to={link.to}
                end={link.to === "/"}
                title={link.label}
                className={({ isActive: exact }) =>
                  [
                    "flex items-center gap-2.5 rounded-md text-[0.94rem] transition-colors",
                    "h-11 w-11 justify-center lg:w-auto lg:justify-start lg:px-3",
                    exact || lit
                      ? "bg-surface-high font-semibold text-ink"
                      : "text-ink-soft hover:bg-surface hover:text-ink",
                  ].join(" ")
                }
              >
                {({ isActive: exact }) => (
                  <>
                    <span
                      className={[
                        "hidden h-4 w-0.5 rounded-full lg:block",
                        exact || lit ? "bg-ink" : "bg-transparent",
                      ].join(" ")}
                    />
                    <link.icon size={18} className={exact || lit ? "text-ink" : "text-ink-mute"} aria-hidden />
                    <span className="hidden lg:inline">{link.label}</span>
                  </>
                )}
              </NavLink>
            </li>
          );
        })}
      </ul>

      <StartAction variant="rail" />

      <div className="mt-auto space-y-4 pt-6">
        <ConnectionNote online={online} pending={pending} syncing={syncing} />
        <NavLink
          to={ACCOUNT.to}
          title={ACCOUNT.label}
          className={[
            "flex items-center gap-3 rounded-md transition-colors",
            "h-11 w-11 justify-center lg:h-auto lg:w-auto lg:justify-start lg:px-2 lg:py-2",
            pathname.startsWith(ACCOUNT.also) ? "bg-surface-high" : "hover:bg-surface",
          ].join(" ")}
        >
          <AccountFace size={36} />
          <AccountName />
        </NavLink>
      </div>
    </nav>
  );
}

function BottomNav() {
  const { pathname } = useLocation();
  const [today, journal, progress] = LINKS as [(typeof LINKS)[number], (typeof LINKS)[number], (typeof LINKS)[number]];
  return (
    <nav className="fixed inset-x-0 bottom-0 z-40 border-t border-line/70 bg-base/85 safe-bottom backdrop-blur-lg md:hidden">
      <ul className="grid grid-cols-5">
        <TabLink link={today} pathname={pathname} />
        <TabLink link={journal} pathname={pathname} />
        <li>
          <StartAction variant="tab" />
        </li>
        <TabLink link={progress} pathname={pathname} />
        <li>
          <NavLink
            to={ACCOUNT.to}
            className={[
              "flex h-14 flex-col items-center justify-center gap-0.5 text-[0.7rem] font-medium",
              pathname.startsWith(ACCOUNT.also) ? "text-ink" : "text-ink-mute",
            ].join(" ")}
          >
            <span
              className={[
                // Flex, not block: around an inline-block avatar, a block grows a
                // line box taller than the picture, and the ring turned oval.
                "flex rounded-full ring-2 ring-offset-1 ring-offset-base",
                pathname.startsWith(ACCOUNT.also) ? "ring-ink" : "ring-transparent",
              ].join(" ")}
            >
              <AccountFace size={22} mark={false} />
            </span>
            <span>{ACCOUNT.label}</span>
          </NavLink>
        </li>
      </ul>
    </nav>
  );
}

function TabLink({ link, pathname }: { link: (typeof LINKS)[number]; pathname: string }) {
  const lit = link.also !== undefined && pathname.startsWith(link.also);
  return (
    <li>
      <NavLink
        to={link.to}
        end={link.to === "/"}
        className={({ isActive: exact }) =>
          [
            // 56px tall: a thumb target that works with a shaking hand.
            "flex h-14 flex-col items-center justify-center gap-0.5 text-[0.7rem] font-medium",
            exact || lit ? "text-ink" : "text-ink-mute",
          ].join(" ")
        }
      >
        {({ isActive: exact }) => (
          <>
            <link.icon size={20} aria-hidden />
            <span className="relative">
              {link.label}
              {(exact || lit) && <span className="absolute -bottom-1.5 left-0 h-0.5 w-full rounded-full bg-ink" />}
            </span>
          </>
        )}
      </NavLink>
    </li>
  );
}

/**
 * The signed-in face. Until the account has loaded — or with the server out
 * of reach on a first launch — the cog, so the way to Réglages never vanishes.
 */
function AccountFace({ size, mark }: { size: number; mark?: boolean }) {
  const account = useQuery({ queryKey: ["account"], queryFn: api.getAccount });
  const user = account.data?.user;
  if (!user) return <Settings size={Math.round(size * 0.55)} className="text-ink-mute" aria-hidden />;
  return <Avatar account={user} size={size} mark={mark} />;
}

/** Name and role beside the face, once the sidebar is wide enough. */
function AccountName() {
  const account = useQuery({ queryKey: ["account"], queryFn: api.getAccount });
  const user = account.data?.user;
  return (
    <span className="hidden min-w-0 lg:block">
      <span className="block truncate text-[0.92rem] font-semibold">{user ? displayName(user) : "Compte"}</span>
      {user && <span className="block text-[0.78rem] text-ink-mute">{ROLE_LABEL[user.role]}</span>}
    </span>
  );
}

/** Only while something waits: a count on the rail, a sentence once the sidebar is wide enough. */
function ConnectionNote({ online, pending, syncing }: { online: boolean; pending: number; syncing: boolean }) {
  // All saved is the normal state: it makes room for the account rather than saying so.
  if (pending === 0 && online) return null;
  const text = syncing
    ? `Envoi de ${pending} séries…`
    : `${pending} série${pending > 1 ? "s" : ""} en attente. Envoi dès que le serveur répond.`;
  return (
    <p className={["text-[0.78rem]", syncing ? "text-ink-soft" : "text-accent"].join(" ")} title={text}>
      <span className="num font-bold lg:hidden">{pending}</span>
      <span className="hidden lg:inline">{text}</span>
    </p>
  );
}

/** Compact version for the phone header. */
export function ConnectionPill() {
  const { pending, syncing } = useConnection();
  const label = pendingLabel(pending, syncing);
  if (label === null) return null;

  return (
    <span className="shrink-0 rounded-full bg-accent px-2.5 py-1 text-[0.72rem] font-semibold text-accent-ink">
      {label}
    </span>
  );
}

/**
 * Sets still waiting with no session under way to carry them in its pill
 * (closed offline, say): up in the same corner, on the phone, where nothing
 * else would say so. The sidebar says it on a wider screen.
 */
function PendingPill({ label }: { label: string | null }) {
  if (label === null) return null;
  return (
    <span className="fixed top-[calc(env(safe-area-inset-top,0px)+0.5rem)] right-3 z-40 rounded-full bg-accent px-3 py-1.5 text-[0.78rem] font-semibold text-accent-ink shadow-lg md:hidden">
      {label}
    </span>
  );
}
